import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  CreditCard,
  CheckCircle,
  Warning,
  ArrowSquareOut,
  XCircle
} from '@phosphor-icons/react';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import useThemeStyles from '../../../theme/useThemeStyles';

function StripeConnectSetup() {
  const styles = useThemeStyles();
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [_onboarding, setOnboarding] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      
      const response = await api.get(`/api/stripe-connect/status/${businessId}`);
      setStatus(response.data);
    } catch (error) {
      setError('Failed to load Stripe Connect status');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.business?._id]);

  useEffect(() => {
    const businessId = selectedBusiness?.business?._id;
    if (businessId) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [selectedBusiness, fetchStatus]);

  const _handleOnboard = async () => {
    try {
      setOnboarding(true);
      setError('');
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setError('Business not found');
        setOnboarding(false);
        return;
      }
      
      const response = await api.post('/api/stripe-connect/onboard', {
        businessId: businessId
      });
      
      // Open Stripe onboarding in new window
      window.open(response.data.onboardingUrl, '_blank');
      
      // Poll for status updates every 3 seconds
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get(`/api/stripe-connect/status/${selectedBusiness._id}`);
          if (statusResponse.data.onboardingCompletedAt) {
            clearInterval(pollInterval);
            setStatus(statusResponse.data);
            setOnboarding(false);
          }
        } catch (error) {
          // Continue polling
        }
      }, 3000);

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setOnboarding(false);
      }, 5 * 60 * 1000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to initiate Stripe onboarding');
      setOnboarding(false);
    }
  };

  const handleDashboardLink = async () => {
    try {
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setError('Business not found');
        return;
      }
      
      const response = await api.post(`/api/stripe-connect/dashboard-link/${businessId}`);
      window.open(response.data.dashboardUrl, '_blank');
    } catch (error) {
      setError('Failed to generate dashboard link');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const _getStatusIcon = () => {
    if (!status?.connected) {
      return <CreditCard size={48} style={{ opacity: 0.5 }} />;
    }
    if (status.accountStatus === 'enabled' && status.chargesEnabled) {
      return <CheckCircle size={48} color="#10b981" />;
    }
    if (status.accountStatus === 'restricted') {
      return <Warning size={48} color="#f59e0b" />;
    }
    return <XCircle size={48} color="#ef4444" />;
  };

  const getStatusMessage = () => {
    if (!status?.connected) {
      return {
        title: 'Stripe Account Not Connected',
        description: 'Connect your Stripe account to enable payment links for invoices. Couples can then pay through Stripe.',
        severity: 'info'
      };
    }
    if (status.accountStatus === 'enabled' && status.chargesEnabled) {
      return {
        title: 'Stripe Account Connected',
        description: 'Your Stripe account is active and ready to accept payments. You can now generate payment links for invoices.',
        severity: 'success'
      };
    }
    if (status.accountStatus === 'restricted') {
      return {
        title: 'Stripe Account Setup Incomplete',
        description: 'Your Stripe account requires additional information. Please complete the onboarding process to enable payment processing.',
        severity: 'warning'
      };
    }
    return {
      title: 'Stripe Account Pending',
      description: 'Your Stripe account is being set up. Please complete the onboarding process.',
      severity: 'warning'
    };
  };

  const _statusInfo = getStatusMessage();

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Stripe Connect Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your Stripe account to enable secure payment processing for invoices. Couples can pay via payment links.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CreditCard size={48} style={{ opacity: 0.5 }} />
          <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
            Payments Feature Coming Soon
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We're working on bringing payment processing to Idotoo. This feature will allow you to accept payments directly through invoices using Stripe Connect. Stay tuned for updates!
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Existing connected accounts can still view their status */}
        {status?.connected && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Account Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Account Status:
                  </Typography>
                  <Chip
                    label={status.accountStatus || 'Unknown'}
                    size="small"
                    color={
                      status.accountStatus === 'enabled' ? 'success' :
                      status.accountStatus === 'restricted' ? 'warning' : 'default'
                    }
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Charges Enabled:
                  </Typography>
                  <Chip
                    label={status.chargesEnabled ? 'Yes' : 'No'}
                    size="small"
                    color={status.chargesEnabled ? 'success' : 'default'}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Payouts Enabled:
                  </Typography>
                  <Chip
                    label={status.payoutsEnabled ? 'Yes' : 'No'}
                    size="small"
                    color={status.payoutsEnabled ? 'success' : 'default'}
                  />
                </Box>
              </Grid>
            </Grid>
            {status.accountStatus === 'enabled' && status.chargesEnabled && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowSquareOut size={20} />}
                  onClick={handleDashboardLink}
                  fullWidth
                >
                  Open Stripe Dashboard
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Connection buttons temporarily hidden - payments feature coming soon */}
        {/* <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {!status?.connected ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<CreditCard size={20} />}
              onClick={handleOnboard}
              disabled={onboarding}
            >
              {onboarding ? 'Opening Stripe...' : 'Connect Stripe Account'}
            </Button>
          ) : status.accountStatus !== 'enabled' || !status.chargesEnabled ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<ArrowSquareOut size={20} />}
              onClick={handleOnboard}
              disabled={onboarding}
            >
              {onboarding ? 'Opening Stripe...' : 'Complete Onboarding'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowSquareOut size={20} />}
              onClick={handleDashboardLink}
            >
              Open Stripe Dashboard
            </Button>
          )}
        </Box> */}
      </Paper>
    </Box>
  );
}

export default StripeConnectSetup;
